// The single definition of "this player cannot play, for anyone".
//
// ONE MODULE, BOTH RUNTIMES. The Vite client imports this via the `@shared` alias
// (vite.config.ts -> supabase/functions/_shared); Deno edge functions import it by
// relative path. That is deliberate and it is the whole point of the file:
// SAB-1159 exists because the client keyed its lock on `mlb_team_id` while
// daily-lineup keyed on `mlb_team_name`, so a half-populated row rendered a swap
// control the server then refused. SAB-751 was two byte-identical copies of a
// status vocabulary fixed in one place and not the other. A second copy of THIS
// list would rebuild that defect a third time.
//
// ── Why a status-based unlock exists at all ──────────────────────────────────
//
// The per-game lock fails closed on an unresolvable team: no team => unknowable
// game state => locked, so nobody can move a player after seeing how his game
// went. That instrument is right, and it stays.
//
// But it was firing on players whose team we could resolve perfectly well --
// 210 rows whose team fields were simply stale, because sync-player-status
// crawls the 30 MLB clubs' 40-man rosters (sportId=1) and a player who is not
// on one is invisible to it. Every one of those 210 turned out to be Released,
// a Free Agent, or Voluntarily Retired: they cannot accrue a stat for anyone.
// Locking them protected nothing and froze a roster slot indefinitely -- one
// user sat on a frozen pitcher through the playoffs.
//
// Fail-closed protects against IGNORANCE about a player's game. Once the team
// and status are backfilled from MLB we are not ignorant: we know exactly what
// these players are. So the null-team lock keeps guarding the unknown case, and
// a KNOWN-unplayable status resolves unlocked.

/**
 * Roster statuses that mean the player cannot appear in any game.
 *
 * Deliberately NARROW. These three are terminal with respect to playing: a
 * released player, a free agent, and a voluntarily retired player are on no
 * club's roster and can be in no lineup.
 *
 * DELIBERATELY EXCLUDED, each of which also cannot play today but is NOT here:
 *
 *   'Reassigned to Minors'        (240 players, 20 rostered in fantasy)
 *   'Designated for Assignment'   (16 / 4)
 *   'Injured 60-Day' and friends  (698 / 36)
 *   'Restricted List', 'Suspended # days', 'Development List'
 *
 * All of those describe a player who is still ON a club. He can be activated,
 * and on the day he is, the club's game is the thing that must lock him. Adding
 * them here would unlock a player who may genuinely play that afternoon, which
 * is the manipulation this lock exists to prevent. Widening this list is a
 * product decision with a real downside -- make it deliberately, not by
 * pattern-matching "also cannot play right now".
 */
export const UNPLAYABLE_STATUSES: ReadonlySet<string> = new Set([
  'Released',
  'Free Agent',
  'Voluntarily Retired List',
]);

/**
 * True when the status PROVES the player cannot play.
 *
 * Returns false for null, undefined, empty, and anything unrecognised -- an
 * unknown status is ignorance, and ignorance must still fail closed through the
 * caller's team check. This function only ever REMOVES a lock, and only on a
 * value we recognise, so an unmapped status can never unlock anybody.
 *
 * Trimmed and case-insensitive: SAB-751 was a Title Case mismatch between what
 * one writer stored and what another compared, and an exact-match check here
 * would inherit exactly that fragility.
 */
export function isKnownUnplayable(status: string | null | undefined): boolean {
  if (typeof status !== 'string') return false;
  const s = status.trim().toLowerCase();
  if (!s) return false;
  for (const known of UNPLAYABLE_STATUSES) {
    if (known.toLowerCase() === s) return true;
  }
  return false;
}
